import {
  InboundReferenceCollection,
  OrderDoc,
  OrderProduct,
  OutboundReferenceCollection,
  PaymentStatusCode,
  ProductInboundTransactionDoc,
  ProductInventoryDoc,
  ProductInventoryHistoryDoc,
  ProductOutboundOperation,
  ProductOutboundTransactionDoc,
  ProductPacking,
  ProductSoldOutTemplate,
  ProductState,
  ProductStateCode,
  ProductTransactionStatus,
  StoreIssueDoc,
  productPackings,
  productStates,
} from '@gooduncles/gu-app-schema';
import {
  ColumnState,
  IRowNode,
  NewValueParams,
  RowClassParams,
  ValueFormatterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { message, notification } from 'antd';
import { addDays } from 'date-fns';
import { Timestamp, deleteField } from 'firebase/firestore';
import { get, has, isNumber, set } from 'lodash-es';
import { senderKey } from 'src/constants';
import { v4 } from 'uuid';

import { errorObjectToString, formatNumber, getLocalStorageValue, setLocalStorageValue } from 'src/utils/common';
import { DateForamt, formatDate, getKakaoTalkRequestOptions } from 'src/utils/date-util';
import { callSendKakaoAlimTalkForChangeVolume } from 'src/utils/firebase/firebase-callable';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';
import { getAlimtalkMessage, getUser } from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';
import { inferEntity } from 'src/utils/audit-collection-map';
import { ConsoleLogger } from 'src/utils/logger';
import { organizeKakaotalkResultMessage } from 'src/utils/nhn-notification-util';
import { calcProductUnitVolume, calculateOrderAmounts } from 'src/utils/order-util';

import { ProductDetailRow } from 'src/hooks/subscription/useProductsWithOptions';

const firebaseManager = FirebaseManager.getInstance();
const logger = ConsoleLogger.getInstance();
const auditLogger = AuditLogger.getInstance();

export const formatNumberForAgCell = (params: any, fraction = 0, replacer = '') => {
  const number = params.value;
  if (number === undefined || number === null) {
    return replacer;
  }
  const toNumber = isNumber(number) ? number : Number(number);
  return isNaN(toNumber) ? replacer : formatNumber(toNumber, fraction);
};

export const formatDateForAgCell = (format: DateForamt) => (params: any) => {
  const timestamp = params.value as Timestamp;
  return timestamp ? formatDate(timestamp.toDate(), format) : '';
};

export const stringDateFormatter = ({ value }: { value: string | null }) =>
  value ? formatDate(value, 'yyyy-MM-dd') : '';

export const defaultValueGetter = (params: any) => {
  const value = (params.colDef.field as string)?.split('.')?.reduce((acc, cur) => acc?.[cur], params.data);
  return value ? value : 'unknown';
};

export const unknownValueCellClass = (params: any) => {
  return params.value === 'unknown' ? 'unknown' : '';
};

const submittedOrderMsg = /주문완료/;
const canceledOrderMsg = /주문취소/;
export const orderStatusCellClass = (params: any) => {
  return submittedOrderMsg.test(params.value) ? 'submitted' : canceledOrderMsg.test(params.value) ? 'canceled' : '';
};

/**
 * @param requiredFields data에서 참조해야하는 필드 path ex) ['a', 'b.c', 'd.e.f']
 * @param validation 추가로 해야하는 검증
 * @returns
 */
export function onValueSetterWithValidation<T extends object>(
  params: ValueSetterParams<T>,
  requiredFields?: string[],
  validation?: (value: any) => boolean,
  options?: {
    type?: 'number' | 'array' | 'boolean';
    /** null값을 업데이트할 것인가? true: yes */
    nullable?: boolean;
    /** 필드 삭제 허용 */
    removable?: boolean;
    /** 필드 삭제 대신 null값으로 변경 */
    nullify?: boolean;
    /** string값을 array로 자를때 사용할 구분자 */
    arrayDelimiter?: string;
  },
) {
  const { type = 'string', nullable = false, arrayDelimiter = ',', removable = false, nullify = false } = options ?? {};
  const data: T | undefined = params.data;

  if (!data) {
    return false;
  }

  if (params.newValue === undefined || params.newValue === null) {
    if (removable) {
      set(params.data, params.colDef.field as string, undefined);
      return true;
    } else if (nullify) {
      set(params.data, params.colDef.field as string, null);
      return true;
    } else {
      message.error('값을 입력해주세요.');
      return false;
    }
  }

  if (validation && !validation(params.newValue)) {
    message.error('값이 올바르지 않습니다.');
    return false;
  }

  if (requiredFields && requiredFields.length > 0) {
    for (const field of requiredFields) {
      if (!has(data, field)) {
        message.error(`필수 필드가 누락되었습니다. field: ${field as string}, 개발자에게 알려주세요.`);
        return false;
      }
    }
  }

  if (type === 'number') {
    const toNumber = Number(params.newValue);
    if (isNaN(toNumber)) {
      message.error('숫자만 입력 가능합니다.');
      return false;
    }
    set(params.data, params.colDef.field as string, toNumber);
  } else if (type === 'array') {
    const newValue = params.newValue
      .split(arrayDelimiter)
      .map((v: string) => v.trim())
      .filter((v: string) => v.length > 0);
    const nullableValue = newValue.length === 0 && nullable ? null : newValue;
    set(params.data, params.colDef.field as string, nullableValue);
  } else if (type === 'string') {
    const nullableValue = params.newValue.length === 0 && nullable ? null : params.newValue;
    set(params.data, params.colDef.field as string, nullableValue);
  }
  return true;
}

/**
 * @param paths field path가 직접적인 경로가 아닌 경우 따로 입력한다.
 */
export function onCellValueChangedWithUpdate<T>(logName: string) {
  return async (
    params: NewValueParams<T>,
    collection: string,
    docId?: string,
    paths?: string,
    removable?: boolean,
    nullable?: boolean,
    // 삭제 시 사용할 값
    removableValue?: any,
  ) => {
    const field = params.colDef.field;
    if (!field) {
      message.error(`수정할 수 없는 필드정보입니다. field: ${field as string}`);
      return;
    }

    const id: string = docId ?? (params.data as any)?._id;
    if (!id) {
      message.error('id값이 없습니다.');
      return;
    }

    const newValue = get(params.data, field);
    if (removable && (newValue == undefined || newValue == null)) {
      try {
        await firebaseManager.updateDoc(
          collection,
          id,
          {
            [paths ?? field]: removableValue ? removableValue : deleteField(),
          },
          { audit: { suppress: true } },
        );
        auditLogger.log({
          action: 'DELETE',
          entity: inferEntity(collection),
          entityId: id,
          summary: `${logName} - ${field} 삭제`,
          changes: [{ field, before: params.oldValue, after: undefined }],
        });
        message.success('변경완료');
      } catch (error: any) {
        console.error(error);
        message.error(error?.message ?? '알 수 없는 에러 발생!');
        auditLogger.log({
          action: 'UPDATE',
          entity: inferEntity(collection),
          entityId: id,
          summary: `${logName} - ${collection}/${id}.${field} 수정 실패`,
          level: 'error',
        });
      }
    }

    if (newValue !== undefined && newValue !== params.oldValue) {
      // null값 허용 여부
      if (newValue === null && !nullable) {
        return;
      }

      try {
        await firebaseManager.updateDoc(collection, id, { [paths ?? field]: newValue }, { audit: { suppress: true } });
        auditLogger.log({
          action: 'UPDATE',
          entity: inferEntity(collection),
          entityId: id,
          summary: `${logName} - ${field} 변경`,
          changes: [{ field, before: params.oldValue, after: newValue }],
        });
        message.success('변경완료');
      } catch (error: any) {
        console.error(error);
        message.error(error?.message ?? '알 수 없는 에러 발생!');
        auditLogger.log({
          action: 'UPDATE',
          entity: inferEntity(collection),
          entityId: id,
          summary: `${logName} - ${collection}/${id}.${field} 수정 실패`,
          level: 'error',
        });
      }
    }
  };
}

/**
 * 배송비를 수정한다.
 */
export function onCellValueChangedWithUpdateForDeliveryFee(logName: string) {
  return async (params: NewValueParams<OrderDoc>) => {
    const orderDoc = params.data;
    const field = params.colDef.field;
    try {
      if (field !== 'deliveryFee') {
        message.error('수정할 수 없는 필드정보입니다.');
        return;
      }

      const deliveryFee = get(params.data, 'deliveryFee') as number;
      const grandTotal = orderDoc.totalAmount + deliveryFee;

      await firebaseManager.updateDoc('order', orderDoc._id, {
        deliveryFee,
        paidAmount: grandTotal,
        grandTotal,
      });
    } catch (error: any) {
      console.error(error);
      message.error(error?.message ?? '알 수 없는 에러 발생!');
      auditLogger.log({
        action: 'UPDATE',
        entity: 'order',
        entityId: orderDoc._id,
        summary: `${logName} - 배송비 수정 실패`,
        level: 'error',
      });
    }
  };
}

/**
 * 주문의 상품을 수정한다.
 */
export function onCellValueChangedWithUpdateForOrderProduct(logName: string) {
  return async (
    params: NewValueParams<
      OrderProduct & {
        order: OrderDoc;
      }
    >,
    skipKakaoMessage?: boolean,
  ) => {
    const order = params.data.order;
    const field = params.colDef.field;
    if (field !== 'volume') {
      message.error('수정할 수 없는 필드정보입니다.');
      return;
    }

    try {
      // 1. 변경된 수량을 주문 내용에 반영
      const newValue = get(params.data, 'volume');
      const newOrderProducts = order.products.map((orderProduct) => {
        if (orderProduct.productId === params.data.productId) {
          return {
            ...orderProduct,
            state: ProductStateCode.STOCK,
            volume: newValue,
          };
        }
        return orderProduct;
      });
      const { totalAmount, grandTotal, paidAmount } = calculateOrderAmounts(newOrderProducts, order.deliveryFee ?? 0);

      // 2. 주문 업데이트
      await firebaseManager.updateDoc('order', order._id, {
        products: newOrderProducts,
        totalAmount,
        paidAmount,
        grandTotal,
      });
      const newUnitVolume = calcProductUnitVolume(params.data.measure, newValue);

      // 3. 변경 내용 기록
      logger.logOrder('주문 상품 수량 변경', {
        orderId: order._id,
        storeId: order.storeId,
        before: order,
        after: {
          ...order,
          products: newOrderProducts,
          totalAmount,
          paidAmount,
          grandTotal,
        },
        reason: '실거래가 반영',
        reasonForUser: `고객님이 구매하신 ${params.data.fullName}의 출고량은 ${newUnitVolume}이며 ${formatNumber(
          params.data.price * newValue,
        )}원으로 적용됩니다. 이용해주셔서 감사합니다.`,
        alimtalkTemplateCode: skipKakaoMessage ? null : 'volume-change',
      });

      notification.success({
        message: '주문 상품 수정 완료',
        description: `총 금액: ${order.grandTotal} -> ${grandTotal}`,
      });

      if (skipKakaoMessage) {
        return;
      }

      // 4. 카톡 메시지 발송
      const messageKey = v4();
      message.open({
        type: 'loading',
        content: '카톡 메시지 발송 요청을 진행합니다...',
        duration: 0,
        key: messageKey,
      });
      const user = await getUser(order.userId);
      if (!user?.userTel) {
        throw new Error('유효한 전화번호를 찾지 못했습니다.');
      }

      if (!senderKey) {
        throw new Error('카카오 알림톡 발송을 위한 senderKey가 없습니다.');
      }

      // 5. 새벽 알림 수신을 꺼리는 고객을 위한 옵션 설정
      const requestDate = user.disableNotificationAtDawn ? getKakaoTalkRequestOptions() : undefined;

      const result = await callSendKakaoAlimTalkForChangeVolume({
        senderKey,
        templateCode: 'volume-change',
        recipientList: [
          {
            recipientNo: user?.userTel ?? '',
            templateParameter: {
              date: formatDate(new Date(order.orderDate), 'L월d일'),
              product: params.data.fullName,
              volume: newUnitVolume,
              price: formatNumber(params.data.price * newValue),
            },
            recipientGroupingKey: user?.storeId,
          },
        ],
        ...(requestDate ? { requestDate } : {}),
      });
      // 요청이 성공하면, 요청 로딩 창을 받는다.
      message.destroy(messageKey);

      if (result.data.result !== 'success') {
        throw new Error(result.data.reason);
      }

      if (!result.data.alimtalkMessageDocId) {
        throw new Error('요청은 성공했으나, ID를 받지 못했습니다.');
      }

      const alimtalkMessageDoc = await getAlimtalkMessage<ProductSoldOutTemplate>(result.data.alimtalkMessageDocId);
      if (!alimtalkMessageDoc) {
        throw new Error('요청은 성공했으나, 메시지 정보를 받지 못했습니다.');
      }

      // 요청 결과를 메시지로 정리한다.
      const description = organizeKakaotalkResultMessage(alimtalkMessageDoc.recipientWithResultList);

      notification.success({
        message: `메시지 ${requestDate ? '⏰ 예약' : '즉시'}발송 요청 완료`,
        description,
        className: 'pre-line-notification',
      });
    } catch (error) {
      console.error(error);
      const errorMessage = errorObjectToString(error);
      message.error(errorMessage ?? '알 수 없는 에러 발생!');
      auditLogger.log({
        action: 'UPDATE',
        entity: 'order',
        entityId: order._id,
        summary: `${logName} - 주문 상품 수정 실패`,
        level: 'error',
      });
    }
  };
}

export async function saveGridState(gridRef: AgGridReact | null, table: string) {
  if (!gridRef) return;
  const columnState = gridRef.columnApi.getColumnState();
  const columnGroupState = gridRef.columnApi.getColumnGroupState();
  const filterState = gridRef.api.getFilterModel();
  setLocalStorageValue(table + '-columState', columnState);
  setLocalStorageValue(table + '-columnGroupState', columnGroupState);
  setLocalStorageValue(table + '-filterState', filterState);
  message.success('설정을 저장했습니다.');
}

export async function loadGridState(gridRef: AgGridReact | null, table: string) {
  if (!gridRef) return;
  const columState = getLocalStorageValue<ColumnState[]>(table + '-columState');
  const columnGroupState = getLocalStorageValue<{ groupId: string; open: boolean }[]>(table + '-columnGroupState');
  const filterState = getLocalStorageValue(table + '-filterState');
  gridRef.columnApi.applyColumnState({ state: columState, defaultState: { sort: null }, applyOrder: true });
  if (columnGroupState) {
    gridRef.columnApi.setColumnGroupState(columnGroupState);
  }
  gridRef.api.setFilterModel(filterState);
  message.success('설정을 불러왔습니다.');
}

export function resetGridState(gridRef: AgGridReact | null, table: string) {
  if (!gridRef) return;
  gridRef.columnApi.resetColumnState();
  gridRef.columnApi.resetColumnGroupState();
  gridRef.api.setFilterModel(null);
  saveGridState(gridRef, table);
  message.success('설정을 초기화했습니다.');
}

export const arrayValueFormatter = ({ value }: any) => (Array.isArray(value) ? value.join(', ') : '');

/**
 * 눌린키가 'Enter'이며 현재 편집중인 셀이면 true를 반환하여 편집 종료를 막는다.
 */
export const suppressEnterKeyEvent = (params: any) => {
  const key = params.event.key;
  return key === 'Enter' && params.editing;
};

export async function onCellDataDeleted<T>(params: NewValueParams<T>, callback: () => Promise<string | void>) {
  const {
    oldValue,
    newValue,
    colDef: { headerName },
  } = params;
  if (oldValue !== newValue && newValue === null) {
    try {
      await callback();
      message.success(`${headerName}을(를) 삭제 완료`);
    } catch (error: any) {
      message.error(error.message);
    }
  }
}

///////////////////////////////////////////////////////////////////////////////////////////////
//
// 발주 관리 관련 함수
//
///////////////////////////////////////////////////////////////////////////////////////////////

/**
 * 매장 이슈의 row style을 반환한다.
 */
export const getStoreIssueRowStyle = (params: RowClassParams<StoreIssueDoc>): any => {
  const { status } = params.node.data ?? {};
  if (status === '보류') {
    return { backgroundColor: '#e06666' };
  }
  if (status === '입력') {
    return { backgroundColor: '#fff2cc' };
  }
  if (status === '완료') {
    return { backgroundColor: '#b7e1cd' };
  }
};

/**
 * 그리드에서 드래그가 끝난 후 호출된다.
 * @param field 순서 정보가 담긴 필드
 */
export const createOnRowDragEndHandler = (collection: string, field: string) => async (params: any) => {
  const gridApi = params?.api as GridApi;
  if (!gridApi) {
    notification.error({
      message: 'grid api를 찾을 수 없습니다.',
    });
    return;
  }

  const chagnedNodes: IRowNode[] = [];
  gridApi.forEachNode((node) => {
    const { rowIndex, data } = node;
    // 1. 순서가 변경된 노드 즉 rowIndex + 1과 data.index가 다른 노드만 추출한다.
    if (rowIndex !== null && rowIndex + 1 !== data[field]) {
      chagnedNodes.push(node);
    }
  });

  if (!chagnedNodes || chagnedNodes?.length === 0) {
    return;
  }

  try {
    const updateList = chagnedNodes.map((node) => {
      const { rowIndex, data } = node;
      return { _id: data._id, [field]: (rowIndex ?? 999) + 1 };
    });
    firebaseManager.batchStart();
    for (const item of updateList) {
      await firebaseManager.updateDoc(
        collection,
        item._id,
        { [field]: item[field] },
        {
          bBatch: true,
        },
      );
    }
    await firebaseManager.batchEnd();
    message.success('순서를 변경했습니다.');
  } catch (error) {
    console.error(error);
    const description = errorObjectToString(error);
    notification.error({
      message: '순서 변경에 실패했습니다.',
      description,
    });
  }
};

/**
 * 결제내역의 상태에 맞는 cell class를 반환한다.
 */
export const getCellClassForPaymentStatus = (params: any) => {
  if (params.value === PaymentStatusCode.COMPLETED) {
    return 'green300';
  } else if (
    [PaymentStatusCode.FAILED, PaymentStatusCode.CANCELLED].includes(params.value as unknown as PaymentStatusCode)
  ) {
    return 'red400';
  }
  return null;
};

/**
 * 매장의 최근 주문일 기간에 따라 셀 스타일을 반환한다.
 */
export const getCellStyleForLatestOrderAt = (params: any) => {
  const latestOrderAt = params.value;
  const weekDaysAgo = -7;
  if (latestOrderAt && new Date(latestOrderAt) < addDays(new Date(), weekDaysAgo * 6)) {
    return { backgroundColor: '#cccccc' } as any;
  } else if (latestOrderAt && new Date(latestOrderAt) < addDays(new Date(), weekDaysAgo * 4)) {
    return { backgroundColor: '#f8d7da' } as any;
  } else if (latestOrderAt && new Date(latestOrderAt) < addDays(new Date(), weekDaysAgo * 2)) {
    return { backgroundColor: '#fff3cd' } as any;
  }
};

/**
 * 매장의 첫 주문일 기간에 따라 셀 스타일을 반환한다.
 */
export const getCellStyleForFirstOrderAt = (params: any) => {
  const { firstOrderAt } = params.node.data ?? {};
  const weekDaysAgo = -7;
  if (firstOrderAt && new Date(firstOrderAt) > addDays(new Date(), weekDaysAgo * 1)) {
    return { backgroundColor: '#9BEB54' } as any;
  } else if (firstOrderAt && new Date(firstOrderAt) > addDays(new Date(), weekDaysAgo * 2)) {
    return { backgroundColor: '#ECF5E2' } as any;
  }
};

/**
 * 상품 상태를 한글로 표시한다.
 */
export const productStateFormatter = <T>(params: ValueFormatterParams<T, ProductState>) =>
  productStates[params.value] || params.value;

/**
 * 상품 패킹을 한글로 표시한다.
 */
export const productPackingFormatter = <T>(params: ValueFormatterParams<T, ProductPacking>) =>
  productPackings[params.value] || params.value;

/**
 * 트랜잭션 상태를 한글로 표시한다.
 */
export const productTransactionStatusFormatter = <T>(params: ValueFormatterParams<T, ProductTransactionStatus>) => {
  switch (params.value) {
    case ProductTransactionStatus.PENDING:
      return '대기 중';
    case ProductTransactionStatus.COMPLETED:
      return '완료';
    case ProductTransactionStatus.REJECTED:
      return '거절됨';
    case ProductTransactionStatus.FAILED:
      return '실패';
    case ProductTransactionStatus.CANCELED:
      return '취소됨';
    default:
      return '';
  }
};

/**
 * 트랜잭션 상태에 따라 셀 스타일을 반환한다.
 */
export const getCellStyleForTransactionStatus = (params: any): { color: string } => {
  const { value } = params;
  if (value === ProductTransactionStatus.PENDING) {
    return { color: 'orange' };
  } else if (value === ProductTransactionStatus.COMPLETED) {
    return { color: 'green' };
  } else if (value === ProductTransactionStatus.REJECTED) {
    return { color: 'red' };
  } else if (value === ProductTransactionStatus.FAILED) {
    return { color: '#d32f2f' };
  } else if (value === ProductTransactionStatus.CANCELED) {
    return { color: '#9e9e9e' };
  }
  return { color: 'inherit' };
};

/**
 * 재고명을 표시한다.
 */
export const inventoryNameFormatter = (params: ValueFormatterParams<ProductDetailRow>) =>
  params.data?.inventory?.inventoryName || 'error';

/**
 * 재고에 연결된 상품이 없는 경우의 스타일을 반환한다.
 */
export const getRowStyleForProductInventory = (params: RowClassParams): any => {
  const { products } = params.data;
  if (products.length === 0) {
    return { backgroundColor: '#f8d7da' };
  }
  return null;
};

/**
 * 재고 수량이 Infinity인 경우 '∞'로 표시한다.
 */
export const inventoryQuantityFormatter = (params: ValueFormatterParams<any>) => {
  const inventory = params.data as ProductInventoryDoc;
  const bundleText =
    inventory.uom.bundle && params.value !== Infinity && params.value > 0
      ? ` (${formatNumber(params.value / inventory.uom.bundle.ratio, 2)})`
      : '';
  return params.value === Infinity ? '∞' : formatNumber(params.value) + bundleText;
};

/**
 * 단위(unit) 값에 따라 다른 색상을 적용하는 스타일 함수
 */
export const getUnitCellStyle = (params: any) => {
  const unit = params.value;
  if (unit === 'ea') {
    return { color: '#1890ff' }; // ea 단위는 파란색
  } else if (unit === 'bundle') {
    return { color: '#9C27B0' }; // bundle 단위는 보라색
  }
  return { color: 'inherit' }; // 기본 색상
};

/**
 * 출고 방식을 표기한다.
 */
export const productOutboundOperationFormatter = (
  params: ValueFormatterParams<ProductOutboundTransactionDoc, ProductOutboundOperation>,
) => {
  switch (params.value) {
    case ProductOutboundOperation.INVENTORY:
      return '재고';
    case ProductOutboundOperation.PURCHASE:
      return '발주';
  }
};
/**
 * 입고처를 표시한다.
 */
export const inboundReferenceCollectionFormatter = (
  params: ValueFormatterParams<ProductInboundTransactionDoc, InboundReferenceCollection>,
) => {
  switch (params.value) {
    case InboundReferenceCollection.PRODUCT_SUPPLIER:
      return '거래처 입고';
    case InboundReferenceCollection.STORE_ISSUE:
      return '매장 이슈';
    case InboundReferenceCollection.INVENTORY_ISSUE:
      return '재고 이슈';
    default:
      return '';
  }
};

/**
 * 출고처를 표시한다.
 */
export const outboundReferenceCollectionFormatter = (
  params: ValueFormatterParams<ProductOutboundTransactionDoc, OutboundReferenceCollection>,
) => {
  switch (params.value) {
    case OutboundReferenceCollection.ORDER:
      return '주문';
    case OutboundReferenceCollection.STORE_ISSUE:
      return '매장 이슈';
    case OutboundReferenceCollection.INVENTORY_ISSUE:
      return '재고 이슈';
    case OutboundReferenceCollection.PRODUCT_SUPPLIER:
      return '거래처 출고';
    default:
      return '';
  }
};

/**
 * 재고 이력의 참조 컬렉션을 표시한다.
 */
export const inventoryHistoryReferenceCollectionFormatter = (
  params: ValueFormatterParams<ProductInventoryHistoryDoc, ProductInventoryHistoryDoc['referenceCollection']>,
) => {
  switch (params.value) {
    case InboundReferenceCollection.PRODUCT_SUPPLIER:
      return '거래처';
    case InboundReferenceCollection.STORE_ISSUE:
      return '매장 이슈';
    case InboundReferenceCollection.INVENTORY_ISSUE:
      return '재고 이슈';
    case OutboundReferenceCollection.ORDER:
      return '주문';
    default:
      return '';
  }
};

/**
 * 날짜의 스타일
 */
export const cellStyleForDate = {
  color: 'var(--gray800)',
  fontSize: '13px',
};

/**
 * 재고 UOM에 cell의 unit이 없는 경우 회색으로 표시합니다.
 */
export const uomUnitCellStyle = (params: any): any => {
  const {
    value,
    colDef: { field },
  } = params;
  if (field.includes('uom.ea') && value === undefined) {
    return { backgroundColor: 'var(--gray200)' };
  } else if (field.includes('uom.bundle') && value === undefined) {
    return { backgroundColor: 'var(--gray200)' };
  }
  return {};
};

export const linkCellStyle = {
  color: 'var(--blue400)',
  cursor: 'pointer',
};
